import React, { useState, useEffect } from 'react';
import { CheckCircle, Download, Home, Mail } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(data);
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        return;
      }

      // If still pending, continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-reset-black flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-reset-gold border-t-transparent mx-auto mb-6"></div>
          <h2 className="text-2xl font-semibold text-white mb-2">Verificando pago...</h2>
          <p className="text-gray-400">Por favor espera mientras confirmamos tu compra.</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-reset-black flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-red-900/20 border border-red-500/50 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-6">
            <span className="text-red-500 text-3xl">×</span>
          </div>
          <h2 className="text-2xl font-semibold text-white mb-2">Error al verificar el pago</h2>
          <p className="text-gray-400 mb-6">No pudimos confirmar tu pago. Por favor, revisa tu email o contacta con soporte.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-reset-gold hover:bg-reset-gold-light text-black px-6 py-3 font-bold transition-all inline-flex items-center gap-2"
          >
            <Home size={18} />
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-reset-black flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        {/* Success Card */}
        <div className="bg-reset-dark border-2 border-reset-gold p-8 md:p-12 text-center">
          {/* Success Icon */}
          <div className="bg-reset-gold/10 border-2 border-reset-gold rounded-full h-20 w-20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-reset-gold" size={48} />
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            ¡Pago completado con éxito!
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Gracias por tu compra. Tu recurso está listo para descargar.
          </p>

          {/* Payment Details */}
          {paymentData && (
            <div className="bg-reset-black border border-reset-gold/20 p-6 mb-8 text-left">
              <h3 className="text-white font-semibold mb-4">Detalles de la compra</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Producto:</span>
                  <span className="text-white font-medium">{paymentData.metadata?.product_name || 'Recurso digital'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Importe:</span>
                  <span className="text-white font-medium">
                    {(paymentData.amount_total / 100).toFixed(2)}€
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Estado:</span>
                  <span className="text-reset-gold font-medium">Pagado</span>
                </div>
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="bg-reset-gold/10 border border-reset-gold/30 p-6 mb-8">
            <div className="flex items-start gap-3 mb-4">
              <Mail className="text-reset-gold shrink-0 mt-1" size={24} />
              <div className="text-left">
                <h4 className="text-white font-semibold mb-2">Próximos pasos</h4>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Te hemos enviado un email a tu dirección con:
                </p>
                <ul className="text-gray-300 text-sm mt-2 space-y-1">
                  <li>• Enlace de descarga del recurso</li>
                  <li>• Recibo de compra</li>
                  <li>• Instrucciones de uso</li>
                </ul>
              </div>
            </div>
            <p className="text-gray-400 text-xs text-left">
              Si no recibes el email en los próximos minutos, revisa tu carpeta de spam o contacta con soporte.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/#recursos')}
              className="bg-reset-gold hover:bg-reset-gold-light text-black px-6 py-3 font-bold transition-all inline-flex items-center justify-center gap-2"
            >
              <Download size={18} />
              Ver más recursos
            </button>
            <button
              onClick={() => navigate('/')}
              className="border-2 border-reset-gold text-reset-gold hover:bg-reset-gold hover:text-black px-6 py-3 font-bold transition-all inline-flex items-center justify-center gap-2"
            >
              <Home size={18} />
              Volver al inicio
            </button>
          </div>

          {/* Support */}
          <p className="text-gray-500 text-sm mt-8">
            ¿Necesitas ayuda? Contacta con nosotros en{' '}
            <a href="mailto:info@tecnoabogado.com" className="text-reset-gold hover:underline">
              info@tecnoabogado.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;